import { useContext, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Spinner } from "@/components/ui/spinner";
import { Alert } from "@/components/ui/alert";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import MerchantContext from "../../../contexts/MerchantContext";
import { trpc } from "../../../trpc";
import { getEventTypeDescription, getEventTypeLabel } from "../shared/utils";
import {
  LEAD_EVENT_TYPES,
  RENTALS_TYPES,
  type LeadEventType,
  type RentalsType,
} from "../shared/types";
import type { DomusCatalogs, DomusEventMapping, DomusCatalogItem } from "./types";
import { VariableHelpTooltip } from "../shared/VariableHelpTooltip";

type FormValues = {
  branch: string;
  bizLine: string;
  contactStatus: string;
  temperature: string;
  followTypeId: string;
  followDescription: string;
};

type RentalsTypeWithDomusConfig = {
  id: RentalsType;
  name: string;
  crmConfiguration?: {
    id: string;
    domusEventsMapping: DomusEventMapping[];
    isFallback?: boolean;
  };
};

const DEFAULT_FORM_VALUES: FormValues = {
  branch: "",
  bizLine: "",
  contactStatus: "",
  temperature: "",
  followTypeId: "",
  followDescription: "",
};

const DomusCrmConfiguration = (): JSX.Element => {
  const { merchantId, merchantType } = useContext(MerchantContext);

  const { data: crmConnection } = trpc.crmSelfConfig.get.useQuery(
    { merchantId: merchantId || "" },
    { enabled: !!merchantId }
  );

  const {
    data: crmConfigurations,
    isLoading: isLoadingConfigurations,
    refetch: refetchConfigurations,
  } = trpc.crmSelfConfig.listCrmConfigurations.useQuery(
    { merchantId: merchantId || "" },
    { enabled: !!merchantId }
  );

  const domusConfigurations = useMemo(
    () => (crmConfigurations || []).filter((config) => config.provider === "domus"),
    [crmConfigurations]
  );

  const fallbackConfiguration = useMemo(
    () => domusConfigurations.find((config) => config.isFallback),
    [domusConfigurations]
  );

  const rentalsTypesWithConfig = useMemo<RentalsTypeWithDomusConfig[]>(() => {
    if (merchantType !== "rentals") {
      return [];
    }
    return RENTALS_TYPES.map((rentalsType) => {
      const config =
        domusConfigurations.find((c) => c.rentalsType === rentalsType.id) || fallbackConfiguration;

      return {
        id: rentalsType.id,
        name: rentalsType.name,
        crmConfiguration: config
          ? {
              id: config.id,
              domusEventsMapping: (config.domusEventsMapping || []) as DomusEventMapping[],
              isFallback: !config.rentalsType && config.isFallback,
            }
          : undefined,
      };
    });
  }, [domusConfigurations, fallbackConfiguration, merchantType]);

  const [catalogs, setCatalogs] = useState<DomusCatalogs | null>(null);
  const [isLoadingCatalogs, setIsLoadingCatalogs] = useState(false);
  const [eventMappings, setEventMappings] = useState<DomusEventMapping[]>([]);
  const [formValuesByEvent, setFormValuesByEvent] = useState<Record<string, FormValues>>({});
  const [editingRentalsType, setEditingRentalsType] = useState<RentalsType | null>(null);
  const [contactStatusesByBizLine, setContactStatusesByBizLine] = useState<
    Record<number, DomusCatalogItem[]>
  >({});
  const [loadingContactStatuses, setLoadingContactStatuses] = useState<Record<string, boolean>>({});

  const utils = trpc.useUtils();

  const getCatalogs = trpc.crmSelfConfig.getDomusCatalogs.useMutation({
    onSuccess: (result) => {
      if (result.success) {
        setCatalogs(result.data as DomusCatalogs);
      } else {
        toast.error("Error", {
          description: result.error || "No se pudieron obtener los catálogos",
        });
      }
      setIsLoadingCatalogs(false);
    },
    onError: (error) => {
      toast.error("Error", {
        description: error.message || "Ocurrió un error al obtener los catálogos",
      });
      setIsLoadingCatalogs(false);
    },
  });

  const createConfiguration = trpc.crmSelfConfig.createCrmConfiguration.useMutation({
    onSuccess: async () => {
      await utils.crmSelfConfig.listCrmConfigurations.invalidate({ merchantId: merchantId || "" });
      toast.success("Configuración creada", {
        description: "La configuración de Domus se guardó correctamente.",
      });
      await refetchConfigurations();
    },
    onError: (error) => {
      toast.error("Error", {
        description: error.message || "Ocurrió un error al guardar la configuración.",
      });
    },
  });

  const updateConfiguration = trpc.crmSelfConfig.updateCrmConfiguration.useMutation({
    onSuccess: async () => {
      await utils.crmSelfConfig.listCrmConfigurations.invalidate({ merchantId: merchantId || "" });
      toast.success("Configuración actualizada", {
        description: "La configuración de Domus se actualizó correctamente.",
      });
      await refetchConfigurations();
    },
    onError: (error) => {
      toast.error("Error", {
        description: error.message || "Ocurrió un error al actualizar la configuración.",
      });
    },
  });

  const getContactStatusByBizLine = trpc.crmSelfConfig.getDomusContactStatusByBizLine.useMutation({
    onSuccess: (result, variables) => {
      if (result.success) {
        setContactStatusesByBizLine((prev) => ({
          ...prev,
          [variables.bizLineId]: result.data,
        }));
      }
      setLoadingContactStatuses((prev) => ({
        ...prev,
        [String(variables.bizLineId)]: false,
      }));
    },
    onError: (error, variables) => {
      toast.error("Error", {
        description: error.message || "Ocurrió un error al obtener los estados.",
      });
      setLoadingContactStatuses((prev) => ({
        ...prev,
        [String(variables.bizLineId)]: false,
      }));
    },
  });

  useEffect(() => {
    if (!editingRentalsType) {
      setEventMappings([]);
      setFormValuesByEvent({});
      return;
    }

    const config = rentalsTypesWithConfig.find(
      (rentalsType) => rentalsType.id === editingRentalsType
    )?.crmConfiguration;

    if (!config?.domusEventsMapping) {
      setEventMappings([]);
      setFormValuesByEvent({});
      return;
    }

    const mappings = config.domusEventsMapping as DomusEventMapping[];
    setEventMappings(mappings);

    const formValues: Record<string, FormValues> = {};
    mappings.forEach((mapping) => {
      formValues[mapping.event] = {
        branch: mapping.createLead ? String(mapping.createLead.branch ?? "") : "",
        bizLine: mapping.createLead ? String(mapping.createLead.bizLine ?? "") : "",
        contactStatus: mapping.createLead ? String(mapping.createLead.contactStatus ?? "") : "",
        temperature: mapping.createLead ? (mapping.createLead.temperature ?? "") : "",
        followTypeId: mapping.createFollow ? String(mapping.createFollow.followTypeId ?? "") : "",
        followDescription: mapping.createFollow?.description ?? "",
      };
    });
    setFormValuesByEvent(formValues);
  }, [editingRentalsType, rentalsTypesWithConfig]);

  useEffect(() => {
    const apiKey = crmConnection?.domus?.apiKey;

    if (!apiKey) {
      return;
    }

    if (catalogs || isLoadingCatalogs) {
      return;
    }

    setIsLoadingCatalogs(true);
    getCatalogs.mutate({ apiKey });
  }, [crmConnection, catalogs, isLoadingCatalogs, getCatalogs]);

  // Load contact statuses when bizLine is selected in existing configurations
  // Only make API calls for unique bizLineIds that are not already loaded
  useEffect(() => {
    const apiKey = crmConnection?.domus?.apiKey;
    if (!apiKey || !catalogs) return;

    // Get unique bizLineIds from formValuesByEvent
    const uniqueBizLineIds = new Set<number>();
    Object.values(formValuesByEvent).forEach((values) => {
      if (values.bizLine) {
        const bizLineId = Number(values.bizLine);
        if (bizLineId && !isNaN(bizLineId)) {
          uniqueBizLineIds.add(bizLineId);
        }
      }
    });

    // Only make API call for bizLineIds that are not already loaded and not currently loading
    uniqueBizLineIds.forEach((bizLineId) => {
      if (!contactStatusesByBizLine[bizLineId]) {
        const loadingKey = String(bizLineId);
        if (!loadingContactStatuses[loadingKey]) {
          setLoadingContactStatuses((prev) => ({
            ...prev,
            [loadingKey]: true,
          }));

          getContactStatusByBizLine.mutate({
            apiKey,
            bizLineId,
          });
        }
      }
    });
  }, [
    formValuesByEvent,
    catalogs,
    crmConnection,
    contactStatusesByBizLine,
    loadingContactStatuses,
    getContactStatusByBizLine,
  ]);

  const getAvailableEventTypes = (): LeadEventType[] => {
    const usedEvents = new Set(eventMappings.map((m) => m.event));
    return LEAD_EVENT_TYPES.filter((event) => !usedEvents.has(event));
  };

  const handleAddEventType = (eventType: LeadEventType) => {
    setEventMappings((prev) => [
      ...prev,
      {
        event: eventType,
      },
    ]);
    setFormValuesByEvent((prev) => ({
      ...prev,
      [eventType]: { ...DEFAULT_FORM_VALUES },
    }));
  };

  const handleRemoveEventType = (eventType: LeadEventType) => {
    setEventMappings((prev) => prev.filter((m) => m.event !== eventType));
    setFormValuesByEvent((prev) => {
      const updated = { ...prev };
      delete updated[eventType];
      return updated;
    });
  };

  const handleToggleCreateLead = (eventType: LeadEventType, enabled: boolean) => {
    setEventMappings((prev) => {
      const existing = prev.find((m) => m.event === eventType);
      if (!existing) return prev;

      if (enabled) {
        return prev.map((m) =>
          m.event === eventType
            ? {
                ...m,
                createLead: {
                  branch: 0,
                  bizLine: 0,
                  contactStatus: 0,
                  temperature: "20",
                },
              }
            : m
        );
      } else {
        return prev.map((m) => {
          if (m.event === eventType) {
            const { createLead, createFollow, ...rest } = m;
            return rest;
          }
          return m;
        });
      }
    });
  };

  const handleToggleCreateFollow = (eventType: LeadEventType, enabled: boolean) => {
    setEventMappings((prev) =>
      prev.map((m) => {
        if (m.event !== eventType) return m;
        if (enabled) {
          return { ...m, createFollow: { followTypeId: 0 } };
        }
        const { createFollow, ...rest } = m;
        return rest;
      })
    );
    if (!enabled) {
      setFormValuesByEvent((prev) => ({
        ...prev,
        [eventType]: {
          ...(prev[eventType] || DEFAULT_FORM_VALUES),
          followTypeId: "",
          followDescription: "",
        },
      }));
    }
  };

  const handleInputChange = (eventType: LeadEventType, field: keyof FormValues, value: string) => {
    setFormValuesByEvent((prev) => {
      const currentValues = prev[eventType] || DEFAULT_FORM_VALUES;
      const updatedValues = {
        ...currentValues,
        [field]: value,
      };

      // If bizLine changes, reset contactStatus and load new contact statuses
      if (field === "bizLine" && value) {
        updatedValues.contactStatus = "";
        const bizLineId = Number(value);
        const apiKey = crmConnection?.domus?.apiKey;

        if (apiKey && bizLineId && !contactStatusesByBizLine[bizLineId]) {
          setLoadingContactStatuses((prev) => ({
            ...prev,
            [String(bizLineId)]: true,
          }));

          getContactStatusByBizLine.mutate({
            apiKey,
            bizLineId,
          });
        }
      }

      return {
        ...prev,
        [eventType]: updatedValues,
      };
    });
  };

  const handleSelectRentalsType = (rentalsTypeId: RentalsType) => {
    setEditingRentalsType(rentalsTypeId);
  };

  const handleSave = async () => {
    if (!merchantId || !crmConnection?.provider || !editingRentalsType) {
      if (!editingRentalsType) {
        toast.warning("Selecciona un tipo de negocio", {
          description: "Elige un tipo de negocio para configurar Domus.",
        });
      }
      return;
    }

    const domusEventsMapping: DomusEventMapping[] = [];

    for (const mapping of eventMappings) {
      const formValues = formValuesByEvent[mapping.event] || DEFAULT_FORM_VALUES;
      const entry: DomusEventMapping = { event: mapping.event };

      if (mapping.createLead) {
        const requiredFields: Array<{ field: keyof FormValues; label: string }> = [
          { field: "branch", label: "Sucursal" },
          { field: "bizLine", label: "Lineas de Negocio" },
          { field: "contactStatus", label: "Estado por Linea de Negocio" },
          { field: "temperature", label: "Temperatura" },
        ];

        for (const { field, label } of requiredFields) {
          if (!formValues[field]) {
            toast.error("Campos requeridos", {
              description: `Para el evento "${getEventTypeLabel(
                mapping.event
              )}", el campo "${label}" es obligatorio.`,
            });
            return;
          }
        }

        const branchItem = catalogs?.branches.find(
          (branch) => String(branch.id) === formValues.branch
        );

        entry.createLead = {
          branch: Number(formValues.branch),
          branchName: branchItem?.name,
          bizLine: Number(formValues.bizLine),
          contactStatus: Number(formValues.contactStatus),
          temperature: formValues.temperature,
        };
      }

      if (mapping.createFollow) {
        if (!formValues.followTypeId) {
          toast.error("Campos requeridos", {
            description: `Para el evento "${getEventTypeLabel(
              mapping.event
            )}", el campo "Tipo de Seguimiento" es obligatorio.`,
          });
          return;
        }

        const followTypeItem = catalogs?.followTypes?.find(
          (ft) => String(ft.id) === formValues.followTypeId
        );

        entry.createFollow = {
          followTypeId: Number(formValues.followTypeId),
          followTypeName: followTypeItem?.name,
          description: formValues.followDescription || undefined,
        };
      }

      domusEventsMapping.push(entry);
    }

    const currentConfiguration = rentalsTypesWithConfig.find(
      (rentalsType) => rentalsType.id === editingRentalsType
    )?.crmConfiguration;

    if (currentConfiguration && !currentConfiguration.isFallback) {
      await updateConfiguration.mutateAsync({
        merchantId,
        id: currentConfiguration.id,
        domusEventsMapping,
      });
    } else {
      await createConfiguration.mutateAsync({
        merchantId,
        provider: "domus",
        enabled: true,
        rentalsType: editingRentalsType,
        domusEventsMapping,
      });
    }
  };

  if (isLoadingConfigurations) {
    return (
      <div className="rounded-xl border border-gray-200 bg-white p-6">
        <div className="flex items-center gap-2">
          <Spinner size="sm" />
          <p className="text-sm text-gray-600">
            Cargando configuración...
          </p>
        </div>
      </div>
    );
  }

  if (!crmConnection || crmConnection.provider !== "domus") {
    return (
      <div className="rounded-xl border border-gray-200 bg-white p-6">
        <p className="text-gray-600">Conecta Domus para configurar los eventos.</p>
      </div>
    );
  }

  if (merchantType !== "rentals") {
    return (
      <div className="rounded-xl border border-gray-200 bg-white p-6">
        <p className="text-gray-600">
          La configuración de Domus está disponible para comercios de tipo arriendos.
        </p>
      </div>
    );
  }

  const availableEventTypes = getAvailableEventTypes();
  const selectedRentalsType = editingRentalsType
    ? RENTALS_TYPES.find((type) => type.id === editingRentalsType)
    : null;

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-6">
      <div className="flex flex-col gap-6">
        <div>
          <p className="mb-2 text-lg font-bold">
            Acciones Domus
          </p>
          <p className="text-sm text-gray-600">
            Configura cómo Palomma debe crear o actualizar contactos en Domus cuando se produzcan
            eventos.
          </p>
        </div>

        <Alert status="warning" className="rounded-lg text-sm">
          <div className="flex-1">
            <p className="mb-1 text-sm font-semibold">
              Configuración requerida en Domus
            </p>
            <p className="text-xs">
              Para evitar errores, verifica en tu instancia de Domus que solo sea necesario{" "}
              <span className="font-semibold">
                teléfono o correo para crear leads
              </span>
              , no ambos.
              <span className="mt-2 block text-xs">
                <span className="font-semibold">
                  Cómo configurar:
                </span>{" "}
                En la interfaz de Domus, ve a Configuración → Contactos → Campos obligatorios y
                selecciona "Teléfono o Correo" en lugar de requerir ambos campos.
              </span>
            </p>
          </div>
        </Alert>

        <div>
          <p className="mb-1 text-sm font-semibold">
            Configuración por Tipo de Negocio
          </p>
          <p className="mb-4 text-xs text-gray-500">
            Selecciona el tipo de negocio para el que deseas configurar el mapeo de eventos.
          </p>
          <div className="flex flex-col gap-3">
            {rentalsTypesWithConfig.map((rentalsType) => {
              const configuredEvents = rentalsType.crmConfiguration?.domusEventsMapping || [];
              const isConfigured = configuredEvents.length > 0;
              const isSelected = editingRentalsType === rentalsType.id;
              const eventsSummary = isConfigured
                ? configuredEvents.map((m) => getEventTypeLabel(m.event)).join(", ")
                : "Sin eventos configurados";
              return (
                <div
                  key={rentalsType.id}
                  className={`rounded-md border p-4 ${
                    isSelected ? "border-purple-200 bg-purple-50/25" : "border-gray-200 bg-white"
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-semibold">{rentalsType.name}</p>
                      <p className="text-xs text-gray-500">
                        {eventsSummary}
                      </p>
                      {rentalsType.crmConfiguration?.isFallback && (
                        <p className="text-xs text-orange-500">
                          Configuración heredada (fallback)
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge
                        className={
                          isConfigured
                            ? "bg-green-100 text-green-800 border-green-200"
                            : "bg-yellow-100 text-yellow-800 border-yellow-200"
                        }
                      >
                        {isConfigured ? "Configurado" : "Pendiente"}
                      </Badge>
                      <Button
                        size="sm"
                        variant={isSelected ? "default" : "outline"}
                        onClick={() => handleSelectRentalsType(rentalsType.id)}
                      >
                        {isConfigured ? "Editar" : "Configurar"}
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {editingRentalsType ? (
          <>
            <div>
              <p className="mb-3 text-sm font-semibold">
                Mapeo de eventos · {selectedRentalsType?.name}
              </p>
              <div className="flex flex-col gap-4">
                <div>
                  <p className="mb-2 text-xs font-semibold text-gray-600">
                    Tipos de Eventos
                  </p>
                  {availableEventTypes.length > 0 ? (
                    <div className="grid grid-cols-2 gap-2 md:grid-cols-3">
                      {availableEventTypes.map((eventType) => (
                        <div
                          key={eventType}
                          className="rounded-md border border-gray-200 bg-gray-50 p-2"
                        >
                          <div className="flex items-center justify-between">
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span className="flex-1 cursor-help text-xs">
                                    {getEventTypeLabel(eventType)}
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent>
                                  {getEventTypeDescription(eventType)}
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="size-6"
                              aria-label="Agregar evento"
                              onClick={() => handleAddEventType(eventType)}
                            >
                              <Plus className="size-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs italic text-gray-500">
                      Todos los tipos de eventos han sido agregados
                    </p>
                  )}
                </div>

                {eventMappings.length > 0 && (
                  <div>
                    <p className="mb-2 text-xs font-semibold text-gray-600">
                      Acciones de Domus
                    </p>
                    <div className="flex flex-col gap-3">
                      {eventMappings.map((mapping) => {
                        const hasCreateLead = !!mapping.createLead;
                        const formValues = formValuesByEvent[mapping.event] || DEFAULT_FORM_VALUES;

                        return (
                          <div
                            key={mapping.event}
                            className="rounded-md border border-gray-200 bg-gray-50 p-3"
                          >
                            <div className="flex flex-col gap-3">
                              <div className="flex items-center justify-between">
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <span className="cursor-help text-xs font-semibold text-blue-600">
                                        {getEventTypeLabel(mapping.event)}
                                      </span>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      {getEventTypeDescription(mapping.event)}
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="size-6 text-red-600 hover:text-red-700"
                                  aria-label="Eliminar evento"
                                  onClick={() => handleRemoveEventType(mapping.event)}
                                >
                                  <Trash2 className="size-4" />
                                </Button>
                              </div>
                              <Separator />
                              <div className="flex flex-col gap-2">
                                <div className={`flex items-center gap-2 ${hasCreateLead ? "mb-2" : ""}`}>
                                  <Checkbox
                                    checked={hasCreateLead}
                                    onCheckedChange={(checked) =>
                                      handleToggleCreateLead(mapping.event, checked as boolean)
                                    }
                                  />
                                  <label className="text-xs">Crear o Actualizar Contacto</label>
                                </div>
                                {hasCreateLead && (
                                  <div className="ml-6 mt-2">
                                    {isLoadingCatalogs ? (
                                      <div className="flex items-center gap-2">
                                        <Spinner size="sm" />
                                        <p className="text-xs text-gray-500">
                                          Cargando catálogos...
                                        </p>
                                      </div>
                                    ) : catalogs ? (
                                      <div className="flex flex-col gap-2">
                                        <div className="flex flex-col gap-2">
                                          <Label className="text-xs">
                                            Sucursal <span className="text-red-500">*</span>
                                          </Label>
                                          <select
                                            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                                            value={formValues.branch}
                                            onChange={(e) =>
                                              handleInputChange(
                                                mapping.event,
                                                "branch",
                                                e.target.value
                                              )
                                            }
                                          >
                                            <option value="">Selecciona una sucursal</option>
                                            {catalogs.branches.map((branch) => (
                                              <option key={branch.id} value={branch.id}>
                                                {branch.name}
                                              </option>
                                            ))}
                                          </select>
                                        </div>

                                        <div className="flex flex-col gap-2">
                                          <Label className="text-xs">
                                            Lineas de Negocio <span className="text-red-500">*</span>
                                          </Label>
                                          <select
                                            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                                            value={formValues.bizLine}
                                            onChange={(e) =>
                                              handleInputChange(
                                                mapping.event,
                                                "bizLine",
                                                e.target.value
                                              )
                                            }
                                          >
                                            <option value="">Selecciona una linea de negocio</option>
                                            {catalogs.bizLines.map((bizLine) => (
                                              <option key={bizLine.id} value={bizLine.id}>
                                                {bizLine.name}
                                              </option>
                                            ))}
                                          </select>
                                        </div>

                                        <div className="flex flex-col gap-2">
                                          <Label className="text-xs">
                                            Estado por Linea de Negocio <span className="text-red-500">*</span>
                                          </Label>
                                          <select
                                            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                                            value={formValues.contactStatus}
                                            onChange={(e) =>
                                              handleInputChange(
                                                mapping.event,
                                                "contactStatus",
                                                e.target.value
                                              )
                                            }
                                            disabled={!formValues.bizLine}
                                          >
                                            {formValues.bizLine &&
                                            contactStatusesByBizLine[Number(formValues.bizLine)] ? (
                                              <>
                                                <option value="">Selecciona un estado</option>
                                                {contactStatusesByBizLine[
                                                  Number(formValues.bizLine)
                                                ].map((status) => (
                                                  <option key={status.id} value={status.id}>
                                                    {status.name}
                                                  </option>
                                                ))}
                                              </>
                                            ) : (
                                              <option value="">
                                                Selecciona primero una linea de negocio
                                              </option>
                                            )}
                                          </select>
                                          {formValues.bizLine &&
                                            loadingContactStatuses[formValues.bizLine] && (
                                              <div className="mt-1 flex items-center gap-2">
                                                <Spinner size="sm" />
                                                <p className="text-xs text-gray-500">
                                                  Cargando estados...
                                                </p>
                                              </div>
                                            )}
                                        </div>

                                        <div className="flex flex-col gap-2">
                                          <Label className="text-xs">
                                            Temperatura <span className="text-red-500">*</span>
                                          </Label>
                                          <select
                                            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                                            value={formValues.temperature}
                                            onChange={(e) =>
                                              handleInputChange(
                                                mapping.event,
                                                "temperature",
                                                e.target.value
                                              )
                                            }
                                          >
                                            <option value="">Selecciona una temperatura</option>
                                            {catalogs.temperatures.map((temperature) => (
                                              <option key={temperature.id} value={temperature.name}>
                                                {temperature.name}
                                              </option>
                                            ))}
                                          </select>
                                        </div>
                                      </div>
                                    ) : (
                                      <p className="text-xs text-gray-500">
                                        Necesitas cargar los catálogos para seleccionar los campos.
                                      </p>
                                    )}
                                  </div>
                                )}

                                {hasCreateLead && (
                                  <>
                                    <Separator className="my-1" />
                                    <div className={`flex items-center gap-2 ${!!mapping.createFollow ? "mb-2" : ""}`}>
                                      <Checkbox
                                        checked={!!mapping.createFollow}
                                        onCheckedChange={(checked) =>
                                          handleToggleCreateFollow(mapping.event, checked as boolean)
                                        }
                                      />
                                      <label className="text-xs">Crear Seguimiento</label>
                                    </div>
                                    {mapping.createFollow && (
                                      <div className="ml-6 mt-2">
                                        {catalogs?.followTypes && catalogs.followTypes.length > 0 ? (
                                          <div className="flex flex-col gap-2">
                                            <div className="flex flex-col gap-2">
                                              <Label className="text-xs">
                                                Tipo de Seguimiento <span className="text-red-500">*</span>
                                              </Label>
                                              <select
                                                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                                                value={formValues.followTypeId}
                                                onChange={(e) =>
                                                  handleInputChange(
                                                    mapping.event,
                                                    "followTypeId",
                                                    e.target.value
                                                  )
                                                }
                                              >
                                                <option value="">Selecciona un tipo</option>
                                                {catalogs.followTypes.map((ft) => (
                                                  <option key={ft.id} value={ft.id}>
                                                    {ft.name}
                                                  </option>
                                                ))}
                                              </select>
                                            </div>

                                            <div className="flex flex-col gap-2">
                                              <Label className="text-xs">
                                                Descripción (opcional)
                                              </Label>
                                              <textarea
                                                className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                                                placeholder="Ej: Seguimiento automático. Razón: {transferReason}"
                                                value={formValues.followDescription}
                                                onChange={(e) =>
                                                  handleInputChange(
                                                    mapping.event,
                                                    "followDescription",
                                                    e.target.value
                                                  )
                                                }
                                              />
                                              <VariableHelpTooltip />
                                            </div>
                                          </div>
                                        ) : (
                                          <p className="text-xs text-gray-500">
                                            No se encontraron tipos de seguimiento en Domus.
                                          </p>
                                        )}
                                      </div>
                                    )}
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="flex items-center justify-end gap-3">
              <Button
                onClick={handleSave}
                disabled={createConfiguration.isLoading || updateConfiguration.isLoading}
              >
                Guardar configuración
              </Button>
            </div>
          </>
        ) : (
          <div className="rounded-md border border-dashed border-gray-200 p-4">
            <p className="text-sm text-gray-600">
              Selecciona un tipo de negocio para comenzar a configurar el mapeo de eventos de Domus.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default DomusCrmConfiguration;
